import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const VIEWPORT_MARGIN = 8;
// The press point sits at the menu's top-left, so nudge the panel clear of the
// finger that opened it.
const POINTER_OFFSET = 10;
// The finger is still down when the menu appears; the lift fires a click at the
// press point. Ignore taps until that has passed so nothing auto-selects.
const ARM_DELAY_MS = 350;

/**
 * iOS-style action sheet anchored at a long-press point. Touch only -- on
 * hover-capable pointers the same actions are revealed inline on hover instead.
 */
const BiqLongPressMenu = ({ x, y, items, onClose }) => {
  const menuRef = useRef(null);
  const [placement, setPlacement] = useState(null);
  const [armed, setArmed] = useState(false);

  useLayoutEffect(() => {
    const el = menuRef.current;
    if (!el) return;
    const { width, height } = el.getBoundingClientRect();
    setPlacement({
      left: Math.max(
        VIEWPORT_MARGIN,
        Math.min(x + POINTER_OFFSET, window.innerWidth - width - VIEWPORT_MARGIN)
      ),
      top: Math.max(
        VIEWPORT_MARGIN,
        Math.min(y + POINTER_OFFSET, window.innerHeight - height - VIEWPORT_MARGIN)
      ),
    });
  }, [x, y]);

  useEffect(() => {
    const armTimer = setTimeout(() => setArmed(true), ARM_DELAY_MS);
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('resize', onClose);
    window.addEventListener('scroll', onClose, true);
    return () => {
      clearTimeout(armTimer);
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('resize', onClose);
      window.removeEventListener('scroll', onClose, true);
    };
  }, [onClose]);

  return createPortal(
    <div
      className="fixed inset-0 z-[60]"
      style={{ pointerEvents: armed ? 'auto' : 'none' }}
      onPointerDown={onClose}
      onContextMenu={(e) => e.preventDefault()}
    >
      <div
        ref={menuRef}
        role="menu"
        onPointerDown={(e) => e.stopPropagation()}
        style={{
          left: placement?.left ?? x,
          top: placement?.top ?? y,
          opacity: placement ? 1 : 0,
        }}
        className="fixed min-w-[200px] overflow-hidden rounded-2xl border border-[#E8EBE9] bg-white py-1 shadow-xl dark:border-white/10 dark:bg-[#2a2a2a]"
      >
        {items.map((item) => (
          <button
            key={item.key}
            type="button"
            role="menuitem"
            disabled={item.disabled}
            onClick={() => {
              onClose();
              item.onSelect();
            }}
            className="flex w-full items-center gap-3 px-4 py-3 text-left text-[15px] text-[#282A29] active:bg-black/5 disabled:opacity-40 dark:text-[#f0f5f2] dark:active:bg-white/10"
          >
            {item.icon}
            <span>{item.label}</span>
          </button>
        ))}
      </div>
    </div>,
    document.body
  );
};

export default BiqLongPressMenu;
